import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams, Link } from "react-router-dom";
import TestimonialApi from "../Api/TestimonialApi";
import { toast } from "react-toastify";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";

const TestimonialFormLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        clientName: "",
        title: "Happy client",
        message: "",
        rating: 5,
        status: true,
        displayOrder: 0
    });
    const [errors, setErrors] = useState({});

    const statusOptions = [
        { value: true, label: "Active" },
        { value: false, label: "Inactive" },
    ];

    const ratingOptions = [
        { value: 5, label: "5 Stars" },
        { value: 4, label: "4 Stars" },
        { value: 3, label: "3 Stars" },
        { value: 2, label: "2 Stars" },
        { value: 1, label: "1 Star" },
    ];

    useEffect(() => {
        if (isEditMode) {
            fetchDetails();
        }
    }, [id]);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            const res = await TestimonialApi.getTestimonialById(id);
            if (res.status) {
                const data = res.response.data;
                setFormData({
                    clientName: data.clientName || "",
                    title: data.title || "Happy client",
                    message: data.message || "",
                    rating: data.rating || 5,
                    status: data.status ?? true,
                    displayOrder: data.displayOrder || 0
                });
            }
        } catch (error) {
            console.error("Error fetching testimonial details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === "displayOrder") {
            newValue = parseInt(value) || 0;
        }

        setFormData(prev => ({ ...prev, [name]: newValue }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleKeyDown = (e) => {
        // Block signs like + and - as well as 'e' for scientific notation
        if (["e", "E", "+", "-"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : (name === 'rating' ? 5 : true) }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.clientName.trim()) newErrors.clientName = "Client Name is required";
        if (!formData.title.trim()) newErrors.title = "Title is required";
        if (!formData.message.trim()) newErrors.message = "Review/Message is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            const payload = { 
                ...formData
            };

            let res;
            if (isEditMode) {
                res = await TestimonialApi.updateTestimonial(id, payload);
            } else {
                res = await TestimonialApi.createTestimonial(payload);
            }

            if (res.status) {
                // toast.success(isEditMode ? "Testimonial updated successfully" : "Testimonial added successfully");
                navigate("/testimonials");
            }
        } catch (error) {
            console.error("Error submitting testimonial:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0" style={{ color: '#364E3D' }}>{isEditMode ? "Edit Testimonial" : "Add New Testimonial"}</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit} noValidate className="pb-40">
                    <div className="row gy-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Client Name<span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.clientName ? 'is-invalid' : ''}`}
                                name="clientName"
                                placeholder="Enter Client Name (e.g., Jeyanthi RK)"
                                value={formData.clientName}
                                onChange={handleChange}
                            />
                            {errors.clientName && <small className="text-danger">{errors.clientName}</small>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Title<span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.title ? 'is-invalid' : ''}`}
                                name="title"
                                placeholder="Enter Title (e.g., Happy client)"
                                value={formData.title}
                                onChange={handleChange}
                            />
                            {errors.title && <small className="text-danger">{errors.title}</small>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Rating<span className="text-danger">*</span></label>
                            <Select
                                name="rating"
                                options={ratingOptions}
                                value={ratingOptions.find((opt) => opt.value === formData.rating)}
                                onChange={handleSelectChange}
                                styles={selectStyles(errors.rating)}
                                isClearable={false}
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Status<span className="text-danger">*</span></label>
                            <Select
                                name="status"
                                options={statusOptions}
                                value={statusOptions.find((opt) => opt.value === formData.status)}
                                onChange={handleSelectChange}
                                styles={selectStyles(errors.status)}
                                isClearable={false}
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold text-secondary-light">Display Order</label>
                            <input
                                type="number"
                                className="form-control radius-8"
                                name="displayOrder"
                                placeholder="Enter Display Order"
                                value={formData.displayOrder}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                min="0"
                            />
                        </div>

                        <div className="col-12 mt-4">
                            <label className="form-label fw-semibold text-secondary-light">Review / Message<span className="text-danger">*</span></label>
                            <textarea
                                className={`form-control radius-8 ${errors.message ? 'is-invalid' : ''}`}
                                name="message"
                                rows="5"
                                placeholder="Enter Review/Message"
                                value={formData.message}
                                onChange={handleChange}
                            />
                            {errors.message && <small className="text-danger">{errors.message}</small>}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-24">
                        <Link to="/testimonials" className="btn btn-outline-danger-600 px-32 radius-8 justify-content-center d-flex align-items-center" style={{ width: "120px", height: "48px" }}>Cancel</Link>
                        <button type="submit" className="btn btn-primary radius-8 px-18 py-11 justify-content-center d-flex align-items-center" disabled={loading} style={{ backgroundColor: "#364e3d", borderColor: "#364e3d", width: "120px", height: "48px" }}>
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TestimonialFormLayer;

